import { all, put, takeEvery, call } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/core';

import {
    getWorkspaces as getWorkspacesApi,
    createWorkspace as createWorkspaceApi,
    switchWorkspace as switchWorkspaceApi,
    getMembers as getMembersApi,
    inviteMember as inviteMemberApi,
    removeMember as removeMemberApi,
    updateMemberRole as updateMemberRoleApi,
    cancelInvitation as cancelInvitationApi,
    acceptInvitation as acceptInvitationApi,
} from '../../helpers/api/workspace';

import { workspaceApiResponseSuccess, workspaceApiResponseError } from './actions';
import { WorkspaceActionTypes } from './constants';
import { setWorkspaceIdHeader } from '../../helpers/api/apiCore';

// ─── Sagas ──────────────────────────────────────────────────────────

function* fetchWorkspaces(): SagaIterator {
    try {
        const response = yield call(getWorkspacesApi);
        yield put(workspaceApiResponseSuccess(WorkspaceActionTypes.FETCH_WORKSPACES, response.data));
    } catch (error: any) {
        yield put(workspaceApiResponseError(WorkspaceActionTypes.FETCH_WORKSPACES, error));
    }
}

function* switchWorkspace({ payload }: any): SagaIterator {
    try {
        const response = yield call(switchWorkspaceApi, payload.workspaceId);
        const workspace = response.data?.workspace;

        if (workspace) {
            setWorkspaceIdHeader(workspace.id);
        }

        yield put(workspaceApiResponseSuccess(WorkspaceActionTypes.SWITCH_WORKSPACE, response.data));
    } catch (error: any) {
        yield put(workspaceApiResponseError(WorkspaceActionTypes.SWITCH_WORKSPACE, error));
    }
}

function* createWorkspace({ payload }: any): SagaIterator {
    try {
        const response = yield call(createWorkspaceApi, payload);
        yield put(workspaceApiResponseSuccess(WorkspaceActionTypes.CREATE_WORKSPACE, response.data));
    } catch (error: any) {
        yield put(workspaceApiResponseError(WorkspaceActionTypes.CREATE_WORKSPACE, error));
    }
}

function* fetchMembers(): SagaIterator {
    try {
        const response = yield call(getMembersApi, '');
        yield put(workspaceApiResponseSuccess(WorkspaceActionTypes.FETCH_MEMBERS, response.data));
    } catch (error: any) {
        yield put(workspaceApiResponseError(WorkspaceActionTypes.FETCH_MEMBERS, error));
    }
}

function* inviteMember({ payload }: any): SagaIterator {
    try {
        const response = yield call(inviteMemberApi, { email: payload.email, role: payload.role }, '');
        yield put(workspaceApiResponseSuccess(WorkspaceActionTypes.INVITE_MEMBER, response.data));
    } catch (error: any) {
        yield put(workspaceApiResponseError(WorkspaceActionTypes.INVITE_MEMBER, error));
    }
}

function* removeMember({ payload }: any): SagaIterator {
    try {
        const response = yield call(removeMemberApi, payload.userId);
        yield put(workspaceApiResponseSuccess(WorkspaceActionTypes.REMOVE_MEMBER, {
            ...response.data,
            userId: payload.userId,
        }));
    } catch (error: any) {
        yield put(workspaceApiResponseError(WorkspaceActionTypes.REMOVE_MEMBER, error));
    }
}

function* updateMemberRole({ payload }: any): SagaIterator {
    try {
        const response = yield call(updateMemberRoleApi, { user_id: payload.userId, role: payload.role });
        yield put(workspaceApiResponseSuccess(WorkspaceActionTypes.UPDATE_ROLE, response.data));
    } catch (error: any) {
        yield put(workspaceApiResponseError(WorkspaceActionTypes.UPDATE_ROLE, error));
    }
}

function* cancelInvitation({ payload }: any): SagaIterator {
    try {
        const response = yield call(cancelInvitationApi, payload.invitationId);
        yield put(workspaceApiResponseSuccess(WorkspaceActionTypes.CANCEL_INVITATION, {
            ...response.data,
            invitationId: payload.invitationId,
        }));
    } catch (error: any) {
        yield put(workspaceApiResponseError(WorkspaceActionTypes.CANCEL_INVITATION, error));
    }
}

function* acceptInvitation({ payload }: any): SagaIterator {
    try {
        const response = yield call(acceptInvitationApi, payload.token);
        yield put(workspaceApiResponseSuccess(WorkspaceActionTypes.ACCEPT_INVITATION, response.data));
    } catch (error: any) {
        yield put(workspaceApiResponseError(WorkspaceActionTypes.ACCEPT_INVITATION, error));
    }
}

// ─── Watchers ───────────────────────────────────────────────────────

function* workspaceSaga() {
    yield all([
        takeEvery(WorkspaceActionTypes.FETCH_WORKSPACES, fetchWorkspaces),
        takeEvery(WorkspaceActionTypes.SWITCH_WORKSPACE, switchWorkspace),
        takeEvery(WorkspaceActionTypes.CREATE_WORKSPACE, createWorkspace),
        takeEvery(WorkspaceActionTypes.FETCH_MEMBERS, fetchMembers),
        takeEvery(WorkspaceActionTypes.INVITE_MEMBER, inviteMember),
        takeEvery(WorkspaceActionTypes.REMOVE_MEMBER, removeMember),
        takeEvery(WorkspaceActionTypes.UPDATE_ROLE, updateMemberRole),
        takeEvery(WorkspaceActionTypes.CANCEL_INVITATION, cancelInvitation),
        takeEvery(WorkspaceActionTypes.ACCEPT_INVITATION, acceptInvitation),
    ]);
}

export default workspaceSaga;
